import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import {
  BasePessoaFisicaUseCase,
  type FetchResult,
} from '@/features/proposta/entities/Comprador/use-case/base-pessoa-fisica-use-case'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { formatRadios } from '@/features/proposta/utils/format-radios'
import type { TCliente } from '@/types/cliente'
import { tipoDoVinculoDoCompradorEnum } from '@/types/proposta'
import { origemTelefone } from '@/utils/schemas/telefone.schema'

type TCpfValidatorResponse =
  | { ok: true }
  | { ok: false; reason: 'SAME_AS_COMPRADOR' }
  | { ok: false; reason: 'MAE_PAI_ALREADY_LINKED' }
export class MaePaiUseCase extends BasePessoaFisicaUseCase<CompradorPessoaFisicaStepSchemaInput['maePai']> {
  constructor(
    private readonly compradores: TCompradoresCadastrados,
    private readonly compradorAtual: CompradorPessoaFisicaStepSchemaInput['dados'],
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
  ) {
    super()
  }

  validarCpfMaePai(cpfRaw: string): TCpfValidatorResponse {
    if (this.cpfIgual(cpfRaw, this.compradorAtual.cpf)) return { ok: false, reason: 'SAME_AS_COMPRADOR' }
    const compradorMenor = this.compradores.find((c) => c.id === this.compradorAtual.id)!

    const haVinculosDuplicados = compradorMenor?.vinculos.some(
      (compradorVinculado) =>
        compradorVinculado.tipo === tipoDoVinculoDoCompradorEnum.MaePai &&
        this.cpfIgual(compradorVinculado.documento, cpfRaw),
    )
    if (haVinculosDuplicados) return { ok: false, reason: 'MAE_PAI_ALREADY_LINKED' }
    return { ok: true }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TCliente, AxiosError>,
  ): FetchResult<CompradorPessoaFisicaStepSchemaInput['maePai']> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) {
      return { type: 'NOT_FOUND', shouldOpenModal: false }
    }

    if (result.isSuccess && result.data) {
      const cliente = result.data
      return {
        type: 'SUCCESS',
        patch: {
          contato: {
            email: cliente.email ?? '',
            telefone: {
              origem: cliente.telefone?.origem,
              numero: cliente.telefone?.numero ?? '',
              principal: true,
            },
          },
          estadoCivil: {
            status: cliente.estadoCivil ?? null,
            regimeCasamento: cliente.regimeCasamento ?? null,
            uniaoEstavel: formatRadios(cliente.possuiUniaoEstavel),
          },
          dados: {
            id: cliente.id,
            cpf: cliente.cpf ?? '',
            nome: cliente.nome,
            email: null,
            profissaoId: cliente.profissao?.id ?? '',
            nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
            maiorDe18Anos: 'sim' as const,
            buscouCpf: true,
          },
          imagem: cliente.imagem ?? null,
          tipo: tipoDoVinculoDoCompradorEnum.MaePai,
          emailsJaVinculados: this.emailsJaVinculados,
        },
      }
    }

    return { type: 'ERROR' }
  }

  static iniciarDefaultValues(
    emailsJaVinculados: Record<string, string>,
    nacionalidadePrincipalId?: string,
  ): CompradorPessoaFisicaStepSchemaInput['maePai'] {
    return {
      emailsJaVinculados,
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
      },
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: null,
        profissaoId: '',
        nacionalidadeId: nacionalidadePrincipalId ?? '',
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
      tipo: tipoDoVinculoDoCompradorEnum.MaePai,
    }
  }

  limparFormulario(): CompradorPessoaFisicaStepSchemaInput['maePai'] {
    return {
      emailsJaVinculados: this.emailsJaVinculados,
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
      },
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: null,
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
      tipo: tipoDoVinculoDoCompradorEnum.MaePai,
    }
  }
}
